// Ordering for book search results.
//
// Every search box in the reader portal asks two sources at once: the books
// already in Reanlo, and Open Library through the book-search function. The two
// lists then have to be put in one order.
//
// The rule is relevance first. Someone typing "seven days in april" must not be
// handed "Seven Days in June" just because that one is already in Reanlo, and
// must not be handed a 2024 reprint of something unrelated just because it is
// newer. Being in Reanlo and being recent are tiebreakers, applied in that
// order, and only between results that answer the query equally well.

// Lowercase, drop punctuation, collapse whitespace. Unicode-aware so accented
// titles compare the way a reader would expect.
export function normaliseTitle(s) {
  return (s || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// How well a book answers what was typed. Higher is better.
//
// The bands are deliberately coarse. Within a band the tiebreakers decide, and
// that is where "already in Reanlo" earns its place — while someone is still
// typing "seven days", every candidate scores the same and the Reanlo book
// leads. The moment they finish typing "seven days in april", an exact match
// outscores it and takes the top.
export function relevance(book, query) {
  const q = normaliseTitle(query);
  if (!q) return 0;

  const title  = normaliseTitle(book?.title);
  const author = normaliseTitle(book?.author_name);

  if (title && title === q)          return 100; // exactly what they typed
  if (title && title.startsWith(q))  return 80;  // still typing the front of it
  if (title && title.includes(q))    return 60;  // the phrase appears in the title

  const words = q.split(' ').filter(Boolean);
  if (title && words.length > 1 && words.every(w => title.includes(w))) return 40; // same words, different order

  if (author && author.includes(q))  return 20;  // they searched the author
  return 0;
}

// A comparable year. The books table carries both publication_year (integer)
// and publication_date (date), and rows populate one or the other; Open Library
// returns publication_year. All three are read here so a book is not treated as
// undated just because the year lives in the other column.
//
// Returns null when nothing is known, rather than 0. Scoring an unknown year as
// zero sank those books below every dated result, which is the opposite of what
// a missing field should cost. The caller decides what unknown is worth.
export function publicationYear(book) {
  if (book?.publication_year != null && book.publication_year !== '') {
    const y = parseInt(book.publication_year, 10);
    if (!Number.isNaN(y)) return y;
  }
  if (book?.publication_date) {
    const y = parseInt(String(book.publication_date).slice(0, 4), 10);
    if (!Number.isNaN(y)) return y;
  }
  return null;
}

const isReanlo = b => (b?.source === 'reanlo' || b?.source === 'platform') ? 1 : 0;

// Outside sources return half-finished records: a title and nothing else, no
// author, no cover. Searching "heart the lover" put a bare `HEART THE LOVER.`
// with an empty byline above Lily King's properly catalogued book, because the
// two score the same on title and nothing else separated them.
//
// A record you cannot identify is a worse answer than one you can, even when
// the titles match equally well. Author first — it is what tells two books
// apart — then cover, which is what the reader actually looks at.
const hasAuthor = b => (b?.author_name || b?.author || '').trim() ? 1 : 0;
const hasCover  = b => (b?.cover_image_url || b?.cover || '') ? 1 : 0;

// Sorts a copy. Relevance, then Reanlo books, then complete records, then
// newest.
//
// `unknownYearAs` is what a book with no year is worth in the final tiebreak.
// It defaults to the current year, so "we do not know" is treated as recent
// rather than ancient and an undated book holds its place instead of sinking.
// A fixed number rather than a special case, because a comparator that returns
// 0 for unknown-versus-known is not transitive and gives unstable orderings.
//
// Completeness sits below `isReanlo` on purpose: our own catalogue still leads
// the outside sources. It only decides between records that are otherwise tied,
// which is exactly the case that was producing the bad answer.
export function orderSearchResults(books, query, unknownYearAs = new Date().getFullYear()) {
  const year = b => publicationYear(b) ?? unknownYearAs;
  return [...(books || [])].sort((a, b) =>
    relevance(b, query) - relevance(a, query)
    || isReanlo(b) - isReanlo(a)
    || hasAuthor(b) - hasAuthor(a)
    || hasCover(b) - hasCover(a)
    || year(b) - year(a)
  );
}

// ── Talking to a source we do not control ────────────────────────────────
//
// Every search box asks Reanlo and an outside source. When the outside source
// is down it does not answer "no" — it stops answering at all, and a bare
// fetch waits for the browser's own connection timeout, which is tens of
// seconds. The Reanlo results are already in hand by then, but the code that
// merges the two lists cannot run until the outside call settles, so a reader
// searching during an outage sees an empty box and assumes we have no books.
//
// Nothing outside Reanlo is worth waiting more than a few seconds for. A
// search that shows Reanlo's own books and drops the rest is a good search;
// one that shows nothing is not.
//
// This is the outermost deadline for a search. The book-search edge function
// gives its own upstreams 2.5s (UPSTREAM_TIMEOUT_MS) so its budget finishes
// inside this one; if this number ever drops below that, the function will
// still be waiting on Open Library when the browser has already hung up.
// Work that is not behind a keystroke should pass its own longer value
// rather than lean on this default.
export const EXTERNAL_SEARCH_TIMEOUT_MS = 4000;

// The deadline for a search box, which is a different job from the default
// above and needs a different number.
//
// 4s was chosen against desktop latency and does not survive a phone. The
// budget has to cover more than the upstream fetch: two legs over the mobile
// network, TLS, and a possible cold start of the edge function, and only then
// the 2.5s the function gives Open Library and Google Books
// (UPSTREAM_TIMEOUT_MS, supabase/functions/book-search/index.ts). At 4s that
// leaves 1.5s for everything that is not the upstream call, which is fine on
// wifi and routinely is not on LTE.
//
// Reported from a phone: "Heart the lover" returned results on desktop and
// "No results found" on LTE, same query, same box.
//
// A search box can afford to wait because it is honest while it waits — the
// spinner is on screen and Reanlo's own results are already rendered beneath
// it. What it cannot afford is to give up early and call the silence an
// answer. Never raise this above the point where a reader would assume the
// page is broken.
//
// Not for anything that blocks a render: BookDetail.jsx:209 sits ahead of
// setLoading(false) and stays on the 4s default deliberately.
export const SEARCH_BOX_TIMEOUT_MS = 8000;

// What a search box should say when it has nothing to show.
//
// "No results found" is a claim about the world: we asked, and this book does
// not exist. It must never be printed when the truth is that we failed to ask.
// A reader who sees it stops looking — that is the whole point of the words —
// so saying it after a timeout sends them away believing their book is not in
// any catalogue.
export const SEARCH_FAILED_MESSAGE =
  "Couldn't reach the book sources. Check your connection and try again.";

// Did every outside source fail?
//
// The function answers 200 with an empty array whether Open Library had no
// match or never answered at all, so `res.ok` cannot tell them apart — this is
// the layer where the difference still exists, carried in `sources`.
//
// Absent `sources` means the deployed function predates this and we cannot
// know, so it is treated as a success: the same behaviour as before, never a
// false alarm. It starts reporting the truth the moment book-search is
// redeployed, with no client change.
export function outsideSourcesFailed(data) {
  const sources = data?.sources;
  if (!sources) return false;
  const verdicts = Object.values(sources);
  return verdicts.length > 0 && verdicts.every(v => v !== 'ok' && v !== 'empty');
}

// fetch with a deadline. `signal` is the caller's own abort signal, used to
// cancel a superseded search; it is honoured alongside the deadline.
//
// AbortSignal.any() would express this in one line but is too new to rely on
// in every browser we serve, so the two signals are wired together by hand.
export function fetchWithTimeout(url, options = {}, ms = EXTERNAL_SEARCH_TIMEOUT_MS) {
  const { signal: outer, ...rest } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);

  const forward = () => controller.abort();
  if (outer) {
    if (outer.aborted) forward();
    else outer.addEventListener('abort', forward, { once: true });
  }

  return fetch(url, { ...rest, signal: controller.signal }).finally(() => {
    clearTimeout(timer);
    outer?.removeEventListener('abort', forward);
  });
}

// ── Calling the book-search function ──────────────────────────────────────
//
// The headers every caller needs, in one place. The Authorization header is
// the reason this exists: a signed-out visitor has no session, and
// `Bearer ${session?.access_token}` then sends the literal string
// "Bearer undefined", which Supabase rejects. Edge functions verify the JWT
// by default, so the call 401s and the outside results silently vanish.
//
// That matters because not every search box is behind a login. /community is
// a public route — guests browse it under the guest gate — and before these
// calls were routed through the function they went straight to Google Books
// and Open Library, which needed no auth at all.
//
// The publishable key is itself a valid anon JWT, so falling back to it keeps
// signed-out visitors working without weakening anything: the function's own
// RLS and logic are unchanged, and the key already ships in the bundle.
export function bookSearchHeaders(session, publishableKey) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session?.access_token || publishableKey}`,
    apikey: publishableKey,
  };
}

// ── Turning a craving into numbers, before match_books ever runs ─────────
//
// This is the OUTER half of one decision, not a number picked on its own.
// The INNER half is CRAVING_EMBED_TIMEOUT_MS (4000ms,
// supabase/functions/embed-craving/index.ts) -- this one has to stay
// strictly above that one, the same relationship EXTERNAL_SEARCH_TIMEOUT_MS
// (4s, above) already has to UPSTREAM_TIMEOUT_MS (2.5s,
// supabase/functions/book-search/index.ts): those two were set
// independently once and the result timed out twice for the same query,
// once uselessly.
//
// 7000ms, not 5000ms as first landed here. The inner deadline only wraps
// the OpenAI fetch itself -- it starts when that fetch starts, not when
// this call does. Everything else in the round trip sits outside it and
// has to fit inside the margin between the two numbers instead: the
// function's own cold start, reading and parsing the request body,
// serialising the response, and both network hops. That margin is also
// carrying a heavier response than the pair it's modelled on --
// text-embedding-3-small's 1536 floats come back at ~30KB (~14KB rounded),
// not the few KB of JSON book-search returns -- and on a slow connection
// that alone can take real time. UPSTREAM_TIMEOUT_MS/EXTERNAL_SEARCH_TIMEOUT_MS
// carries 1.5s of margin for a much smaller response; 1s was not enough
// margin for a 30KB one plus a cold start. Four live curl tests against
// the deployed function came back at 2.64s, 1.43s, 1.63s, 0.85s -- the
// first call after each of the two deploys ran ~0.8-1.2s slower than the
// call immediately after it, consistent with cold start being a real,
// recurring cost the inner deadline never sees. Same lesson as
// SEARCH_BOX_TIMEOUT_MS below, which had to move from 4s to 8s after a
// real phone on LTE hit a wall a desktop test never showed.
//
// It is deliberately its own budget rather than a reuse of
// EXTERNAL_SEARCH_TIMEOUT_MS or SEARCH_BOX_TIMEOUT_MS above -- this covers
// one outbound OpenAI call standing between a keystroke and the first row,
// not a fan-out across outside book sources once a query already reached
// the database. And it must not inherit OPENAI_EMBED_TIMEOUT_MS (10s,
// supabase/functions/_shared/similarity.ts) either: that default is sized
// for embed-book's background enrichment, where the save already completed
// and nobody is watching a spinner. This is a different class of work.
export const CRAVING_SEARCH_TIMEOUT_MS = 7000;

// Embeds typed craving text through embed-craving, or reports why it
// couldn't -- as a discriminated result, never as a thrown error and never
// as a bare empty array, so a failed embed can't be mistaken for a craving
// that genuinely matched nothing once match_books runs. Those are opposite
// outcomes and the empty state in ticket 27 depends on telling them apart.
//
//   { status: 'ok',      embedding: number[] }
//   { status: 'timeout', embedding: null }
//   { status: 'error',   embedding: null }
//
// 'timeout' covers both edges of the paired deadline above: the function's
// own CRAVING_EMBED_TIMEOUT_MS being hit (reported back as its `status`)
// and this call's own outer CRAVING_SEARCH_TIMEOUT_MS being hit first
// (fetchWithTimeout's AbortController.abort() throwing a plain AbortError
// -- no `reason`, so no TimeoutError name the way Deno's own
// AbortSignal.timeout() produces one; same distinction searchOpenLibrary()
// in IKnowWhatIWant.jsx already makes on err.name).
export async function embedCraving(cravingText, session, publishableKey) {
  try {
    const res = await fetchWithTimeout(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/embed-craving`,
      {
        method: 'POST',
        headers: bookSearchHeaders(session, publishableKey),
        body: JSON.stringify({ craving_text: cravingText }),
      },
      CRAVING_SEARCH_TIMEOUT_MS,
    );
    if (!res.ok) return { status: 'error', embedding: null, sourceHash: null };

    const data = await res.json();
    // 1536 is not a style choice -- every embedding column in this schema
    // (cravings, books, reflections) is vector(1536). A short or empty
    // array would otherwise pass this guard and fail later at the
    // database with a dimension error instead of here, where the cause is
    // still visible.
    if (data?.status === 'ok' && Array.isArray(data.embedding) && data.embedding.length === 1536) {
      // sourceHash: embed-craving computes md5(trimmed text) itself and
      // hands it back rather than this file recomputing it -- ticket 16b,
      // "do not invent a second [hash] convention" -- so cravings.
      // embedding_source_hash (CravingBox.jsx) is exactly what came back
      // here, never a browser-side recomputation (Web Crypto has no MD5).
      return { status: 'ok', embedding: data.embedding, sourceHash: data.source_hash ?? null };
    }
    // Mirror the function's own status rather than collapsing it, so
    // 'timeout' survives this hop instead of flattening into 'error'.
    return { status: data?.status === 'timeout' ? 'timeout' : 'error', embedding: null, sourceHash: null };
  } catch (err) {
    return { status: err.name === 'AbortError' ? 'timeout' : 'error', embedding: null, sourceHash: null };
  }
}
